export function rimuoviPunti(valore: string): string {
  return valore.replace(/\./g, '');
}

export function rimuoviPuntiEConvertiInFloat(valore: string): number {
  return parseFloat(rimuoviPunti(valore).replace(',', '.'))
}

/**
 * Converte un importo in formato locale (IT `1.234,50` o EN `1,234.50`) in `number`.
 *
 * Regole:
 *  - Se sono presenti ENTRAMBI i separatori (`.` e `,`), il decimale è l'ultimo dei due
 *    e l'altro è il separatore delle migliaia (rimosso): `1.234,50`→1234.5, `1,234.50`→1234.5.
 *  - Se è presente UN SOLO tipo di separatore, si disambigua sulla forma:
 *      · più occorrenze, oppure una singola occorrenza seguita da ESATTAMENTE 3 cifre e con
 *        1-3 cifre iniziali → è il separatore delle MIGLIAIA (`1.500`→1500, `1.234.567`→1234567);
 *      · altrimenti (1-2 cifre finali) → è il separatore DECIMALE (`30,50`/`30.50`→30.5).
 *
 * Unica fonte di verità per il parsing importi locale-aware (command-parser, form-builder showIf).
 * Ritorna `NaN` se la stringa non contiene un numero valido.
 */
export function parseImportoLocale(valore: string): number {
  const s = valore.trim();
  const haPunto = s.includes('.');
  const haVirgola = s.includes(',');

  if (!haPunto && !haVirgola) {
    return parseFloat(s);
  }

  if (haPunto && haVirgola) {
    // Entrambi presenti: il decimale è l'ultimo dei due, l'altro è migliaia.
    const decimale = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
    const migliaia = decimale === '.' ? ',' : '.';
    return parseFloat(s.split(migliaia).join('').replace(decimale, '.'));
  }

  // Un solo tipo di separatore: distinguere migliaia da decimale sulla forma.
  const sep = haPunto ? '.' : ',';
  const parti = s.split(sep);
  const soloMigliaia =
    parti.length > 2 // più separatori ⇒ sicuramente migliaia (1.234.567)
      ? parti.slice(1).every((p) => p.length === 3)
      : parti[1]?.length === 3; // singolo separatore + gruppo di 3 cifre ⇒ migliaia (1.500)

  if (soloMigliaia) {
    return parseFloat(parti.join(''));
  }
  // Separatore decimale.
  return parseFloat(s.replace(sep, '.'));
}

export function isNotNill(value: any): boolean {
  return value !== undefined && value !== null;
}


const UNITA_IN_LETTERE = ['', 'uno', 'due', 'tre', 'quattro', 'cinque', 'sei', 'sette', 'otto', 'nove', 'dieci',
  'undici', 'dodici', 'tredici', 'quattordici', 'quindici', 'sedici', 'diciassette', 'diciotto', 'diciannove'];
const DECINE_IN_LETTERE = ['', '', 'venti', 'trenta', 'quaranta', 'cinquanta', 'sessanta', 'settanta', 'ottanta', 'novanta'];
const SCAGLIONI_SINGOLARE = ['', 'mille', 'un milione', 'un miliardo', 'un bilione'];
const SCAGLIONI_PLURALE = ['', 'mila', ' milioni', ' miliardi', ' bilioni'];

/** Parola per un numero 1..999, scritta di seguito come in italiano ("trecentoventitré"). */
function centinaiaInLettere(n: number): string {
  const centinaia = Math.floor(n / 100);
  const resto = n % 100;
  let testo = centinaia === 0 ? '' : (centinaia === 1 ? '' : UNITA_IN_LETTERE[centinaia]) + 'cento';
  if (resto === 0) {
    return testo;
  }
  if (resto < 20) {
    return testo + UNITA_IN_LETTERE[resto];
  }
  const unita = resto % 10;
  let decina = DECINE_IN_LETTERE[Math.floor(resto / 10)];
  // Elisione della vocale finale davanti a "uno" e "otto": ventuno, trentotto.
  if (unita === 1 || unita === 8) {
    decina = decina.slice(0, -1);
  }
  // "cento" + "ottanta" -> "centottanta" (non davanti a "otto": "centootto").
  if (centinaia > 0 && resto >= 80 && resto < 90) {
    testo = testo.slice(0, -1);
  }
  return testo + decina + UNITA_IN_LETTERE[unita];
}

/** Migliaia 2..999: "uno" finale si tronca davanti a "mila" (ventunmila, centunmila). */
function migliaiaInLettere(n: number): string {
  return centinaiaInLettere(n).replace(/o?uno$/, 'un') + 'mila';
}

/**
 * Numero intero non negativo in lettere, minuscolo e in una sola parola fino alle migliaia
 * ("duemilacinquecento", "ventitré"); da milioni in su gli scaglioni sono separati da spazio
 * ("un milione duecentomila"). Ritorna `''` se l'input non è una sequenza di cifre.
 */
export function numeroInLettere(valore: string | number): string {
  const cifre = String(valore).trim().replace(/^0+(?=\d)/, '');
  if (!/^\d+$/.test(cifre) || cifre.length > 15) {
    return '';
  }
  if (cifre === '0') {
    return 'zero';
  }
  const gruppi: number[] = [];
  for (let fine = cifre.length; fine > 0; fine -= 3) {
    gruppi.push(parseInt(cifre.slice(Math.max(0, fine - 3), fine), 10));
  }
  // Fino alle migliaia la scrittura è continua; da milioni in su gli scaglioni si separano con uno spazio.
  const bassi = gruppi[1]
    ? (gruppi[1] === 1 ? 'mille' : migliaiaInLettere(gruppi[1])) + (gruppi[0] ? centinaiaInLettere(gruppi[0]) : '')
    : gruppi[0] ? centinaiaInLettere(gruppi[0]) : '';
  const parti: string[] = [];
  for (let i = gruppi.length - 1; i >= 2; i--) {
    if (gruppi[i]) {
      parti.push(gruppi[i] === 1 ? SCAGLIONI_SINGOLARE[i] : centinaiaInLettere(gruppi[i]) + SCAGLIONI_PLURALE[i]);
    }
  }
  if (bassi) {
    parti.push(bassi);
  }
  // "tre" in coda a una parola composta è accentato: ventitré, centotré, milletré (ma non "tre" da solo né "tredici").
  return parti.join(' ').replace(/([a-z]{3,})tre(?= |$)/g, '$1tré');
}

/**
 * Importo in lettere, es. "300" -> "trecento euro", "320.5" -> "trecentoventi euro e cinquanta centesimi".
 */
export function importoInLettere(euro: string | number, centesimi = 0): string {
  const interi = numeroInLettere(euro || '0');
  if (!interi) {
    return '';
  }
  // "un milione di euro": dopo milioni o miliardi esatti la valuta vuole il "di".
  const di = /(milione|milioni|miliardo|miliardi|bilione|bilioni)$/.test(interi) ? ' di' : '';
  let testo = `${interi === 'uno' ? 'un' : interi}${di} euro`;
  if (centesimi > 0) {
    testo += ` e ${centesimi === 1 ? 'un' : numeroInLettere(centesimi)} ${centesimi === 1 ? 'centesimo' : 'centesimi'}`;
  }
  return testo;
}
